import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import {
  faBrain,
  faChevronDown,
  faChevronUp,
  faCode,
  faCopy,
  faInfoCircle,
  faLightbulb,
  faProjectDiagram,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CLEAN_CHAIN_NAMES } from 'blockscope/static/supportedChains';

import { capitalizeFirstLetter } from 'blockscope/utils/stringUtils';

import StreamTextDisplay from 'blockscope/components/aiSystem/panel/StreamTextDisplay';

/**
 * Renders specialized tracer node messages with expandable sections
 * @param {Object} props - Component props
 * @param {Object|string} props.content - Message content
 */
const TracerNodeMessage = ({ content }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);

  // Parse content if it's a string
  let parsedContent;
  if (typeof content === 'string') {
    try {
      parsedContent = JSON.parse(content);
    } catch (e) {
      // If parsing fails, just display the content as text
      return <StreamTextDisplay text={content} />;
    }
  } else if (typeof content === 'object') {
    parsedContent = content;
  } else {
    return <StreamTextDisplay text={String(content)} />;
  }

  // Extract data from parsed content
  const { messageText, internalThought, addressesToExpand } = parsedContent;

  // Function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '#dc3545'; // Red
      case 'medium':
        return '#fd7e14'; // Orange
      case 'low':
        return '#17a2b8'; // Blue
      default:
        return '#6c757d'; // Gray
    }
  };

  return (
    <div className='tracer-node-container'>
      {/* Main message */}
      <div className='mb-3'>
        <StreamTextDisplay text={messageText || 'No message text available'} />
      </div>
      {/* Analysis section - collapsible */}
      {internalThought && (
        <div className='mb-3'>
          <div
            className={`d-flex align-items-center py-2 px-3 rounded cursor-pointer ${
              showAnalysis ? 'bg-info bg-opacity-10' : 'bg-light'
            }`}
            onClick={() => setShowAnalysis(!showAnalysis)}
          >
            <FontAwesomeIcon icon={faBrain} className='me-2 text-primary' />
            <span className='fw-medium'>Analysis Details</span>
            <FontAwesomeIcon
              icon={showAnalysis ? faChevronUp : faChevronDown}
              className='ms-auto text-primary'
            />
          </div>

          {showAnalysis && (
            <div className='content-body mt-2 px-3 py-2 border-start border-4 border-light'>
              <div className='mb-3'>
                <h6 className='mb-2'>Internal Thought Process</h6>
                <div className='ps-3 py-2 bg-light rounded'>
                  <StreamTextDisplay text={internalThought} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Addresses section - collapsible */}
      {addressesToExpand && addressesToExpand.length > 0 && (
        <div className='mb-3'>
          <div
            className={`d-flex align-items-center py-2 px-3 rounded cursor-pointer ${
              showAddresses ? 'bg-info bg-opacity-10' : 'bg-light'
            }`}
            onClick={() => setShowAddresses(!showAddresses)}
          >
            <FontAwesomeIcon
              icon={faProjectDiagram}
              className='me-2 text-primary'
            />
            <span className='fw-medium'>{`Addresses to Investigate (${addressesToExpand.length})`}</span>
            <FontAwesomeIcon
              icon={showAddresses ? faChevronUp : faChevronDown}
              className='ms-auto text-primary'
            />
          </div>

          {showAddresses && (
            <div className='content-body mt-2 px-3'>
              {addressesToExpand.map((item, index) => (
                <div
                  key={index}
                  className='mb-3 shadow-sm overflow-hidden'
                  style={{ borderRadius: '8px', border: '1px solid #dee2e6' }}
                >
                  <div className='d-flex flex-wrap p-3 bg-light border-bottom'>
                    <div className='d-flex align-items-center flex-grow-1 flex-wrap'>
                      <code className='text-break small'>{item.address}</code>
                    </div>
                    <div className='d-flex ms-auto mt-2 mt-sm-0'>
                      <div className='d-flex align-items-center me-3'>
                        <span className='text-muted small me-1'>Priority:</span>
                        <span
                          className='small'
                          style={{
                            color: getPriorityColor(item.priority),
                            fontWeight: '500',
                          }}
                        >
                          {capitalizeFirstLetter(item.priority)}
                        </span>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted small me-1'>Chain:</span>
                        <span
                          className='small'
                          style={{
                            color: '#6c757d',
                            fontWeight: '500',
                          }}
                        >
                          {CLEAN_CHAIN_NAMES[item.chain]}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='p-3 bg-white'>
                    <div className='mb-3'>
                      <h6 className='mb-2 d-flex align-items-center'>
                        <span>Reason</span>
                      </h6>
                      <div className='bg-light p-3 rounded text-break small'>
                        {item.reason}
                      </div>
                    </div>
                    <div>
                      <h6 className='mb-2 d-flex align-items-center'>
                        <span>Potential Insights</span>
                      </h6>
                      <div className='bg-light p-3 rounded text-break small'>
                        {item.potential}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Raw JSON view toggle - Updated to match the Thought Process button style */}
      <div className='text-end internal-thought-section mb-2'>
        <small
          className={`d-inline-flex align-items-center px-2 py-1 rounded cursor-pointer ${
            showRawJson ? 'bg-primary bg-opacity-10' : 'bg-light'
          }`}
          onClick={() => setShowRawJson(!showRawJson)}
          style={{ cursor: 'pointer', fontSize: '0.85rem' }}
        >
          <FontAwesomeIcon
            icon={faCode}
            className={`me-1 ${
              showRawJson ? 'text-primary' : 'text-secondary'
            }`}
            style={{ fontSize: '0.8rem' }}
          />
          <span>Raw JSON</span>
        </small>
      </div>
      {/* Raw JSON content */}
      {showRawJson && (
        <div className='bg-dark text-white mb-3 mt-2 p-3 rounded'>
          <div className='bg-dark text-white mb-2'>
            <strong>Raw Response JSON</strong>
          </div>
          <pre className='m-0 p-2 small text-secondary'>
            {JSON.stringify(parsedContent, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TracerNodeMessage;

